import { apiCall } from 'src/utils/ApiCall';
import {
  GetControlSummaryDataOutput,
  RecordAttributes,
} from '@amzn/controllershiphub-typescript-client';
import { filterAndCleanRecords } from 'src/components/pages/SummaryPage';
import { CONTROL_APIS, ApiError, UI_PAGE_SIZE } from 'src/common/config/ApiConstants';

interface LazyLoadDataResponse {
  records: RecordAttributes[][];
  totalRecords: number;
  searchAfter?: string[];
  distinctValues?: Record<string, string[]>;
}

export const getLazyLoadControlData = async (
  controlId: string,
  filters: Record<string, string[]> = {},
  searchAfter?: string[]
): Promise<LazyLoadDataResponse> => {
  try {
    let allRecords: RecordAttributes[][] = [];
    let currentSearchAfter = searchAfter;
    let distinctValues: Record<string, string[]> | undefined;
    const targetLimit = controlId === 'grc18956openpo' ? UI_PAGE_SIZE : CONTROL_APIS.BATCH_SIZE;

    do {
      const queryParams = {
        limit: targetLimit.toString(),
        ...(Object.keys(filters).length > 0 && {
          filters: JSON.stringify(filters),
        }),
        ...(currentSearchAfter &&
          currentSearchAfter.length > 0 && { exclusiveStartKey: currentSearchAfter.join(',') }),
        ...(controlId === 'grc18956openpo' && { getDistinctValues: 'false' }),
      };

      const response = await apiCall<GetControlSummaryDataOutput>({
        endpoint: CONTROL_APIS.ENDPOINTS.DATA(controlId),
        queryParams,
      });

      const records = response.controlData?.records || [];
      allRecords = allRecords.concat(records);

      if (!distinctValues && response.distinctValues) {
        distinctValues = response.distinctValues;
      }

      currentSearchAfter = response.lastEvaluatedKey ? [response.lastEvaluatedKey] : undefined;
    } while (currentSearchAfter && allRecords.length < targetLimit);

    const cleanedRecords = filterAndCleanRecords(allRecords);

    return {
      records: cleanedRecords,
      totalRecords: cleanedRecords.length,
      searchAfter: currentSearchAfter,
      distinctValues,
    };
  } catch (err: unknown) {
    const error = err as Error;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error(CONTROL_APIS.ERROR_MESSAGES.UNAUTHORIZED);
    }
    throw new Error(CONTROL_APIS.ERROR_MESSAGES.DATA_FETCH_FAILED);
  }
};
